import React, { useState, useEffect } from "react";
import { Building, BookOpen, Clock, Users, Check, X, Calendar, FileSpreadsheet, AlertCircle } from "lucide-react";
import { 
  fetchBranchList, 
  fetchBranchCourseList, 
  fetchCourseList, 
  fetchBatchList,
  getCourseStudentDetails
} from "../../services/masterService";
import { getAttendanceReport } from "../../services/attendanceService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./Attendance.css";

const AttendanceReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Data states
  const [branches, setBranches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [reportData, setReportData] = useState([]);

  // Selections
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(""); // "" means All

  // Dates (Default to current month)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Load initial branches
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setIsLoading(true);
        const res = await fetchBranchList();
        setBranches(res.data || []);
      } catch (err) {
        setErrorMsg("Failed to load branches.");
      } finally {
        setIsLoading(false);
      }
    };
    loadBranches();
  }, []);

  // When branch changes, fetch mapped courses
  const handleBranchChange = async (branchId) => {
    setSelectedBranch(branchId);
    setSelectedCourse("");
    setSelectedBatch("");
    setSelectedStudent("");
    setCourses([]);
    setBatches([]);
    setStudents([]);
    setReportData([]);

    if (!branchId) return;

    try {
      setIsLoading(true);
      const [bcRes] = await Promise.all([
        fetchBranchCourseList(),
        fetchCourseList()
      ]);
      const branchData = (bcRes.data || []).find(item => String(item.BranchId) === String(branchId));
      if (branchData && branchData.CourseList) {
        setCourses(branchData.CourseList);
      }
    } catch (err) {
      setErrorMsg("Failed to load courses.");
    } finally {
      setIsLoading(false);
    }
  };

  // When course changes, fetch batches and students
  const handleCourseChange = async (branchCourseId) => {
    setSelectedCourse(branchCourseId);
    setSelectedBatch("");
    setSelectedStudent("");
    setBatches([]);
    setStudents([]);
    setReportData([]);

    if (!branchCourseId) return;

    try {
      setIsLoading(true);
      const [batchRes, studentRes] = await Promise.all([
        fetchBatchList(),
        getCourseStudentDetails(branchCourseId)
      ]);

      const filteredBatches = (batchRes.data || []).filter(
        b => String(b.BranchCourseId) === String(branchCourseId)
      );
      setBatches(filteredBatches);

      const studentData = studentRes.data || studentRes || [];
      setStudents(Array.isArray(studentData) ? studentData : []);
    } catch (err) {
      setErrorMsg("Failed to load batches and students.");
    } finally {
      setIsLoading(false);
    }
  };

  // When batch changes
  const handleBatchChange = (batchId) => {
    setSelectedBatch(batchId);
    setSelectedStudent("");
    setReportData([]);
  };

  const getFilteredStudents = () => {
    if (!selectedBatch) return [];
    return students.filter(s => String(s.BatchId) === String(selectedBatch));
  };

  const batchStudents = getFilteredStudents();

  // Generate Report
  const handleGenerateReport = async () => {
    if (!selectedBatch) {
      setErrorMsg("Please select a batch to generate the report.");
      return;
    }
    if (!startDate || !endDate) {
      setErrorMsg("Please select both start and end dates.");
      return;
    }

    try {
      setIsLoading(true);
      setErrorMsg("");
      const payload = {
        batchId: Number(selectedBatch),
        courseDetailsId: selectedStudent ? Number(selectedStudent) : null,
        startDate: startDate,
        endDate: endDate
      };

      const res = await getAttendanceReport(payload);
      setReportData(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to generate report.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (reportData.length === 0) return;

    const exportData = reportData.map((r, idx) => {
      const d = new Date(r.attendanceDate || r.AttendanceDate).toLocaleDateString();
      return {
        "S.No": idx + 1,
        "Date": d,
        "Student ID": r.studentId || r.StudentId || "N/A",
        "Student Name": r.studentName || r.StudentName || "N/A",
        "Status": (r.isPresent || r.IsPresent) ? "Present" : "Absent"
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
    
    saveAs(fileData, `Attendance_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="attendance-explorer">
      <div className="explorer-header">
        <div className="header-left-area">
          <h2>Attendance Report Dashboard</h2>
        </div>
      </div>

      {errorMsg && (
        <div className="explorer-error-alert" style={{ margin: "20px" }}>
          <AlertCircle size={20} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Filter Panel */}
      <div className="roster-top-panel" style={{ margin: "0 20px" }}>
        <div className="roster-filter-actions" style={{ flexWrap: "wrap", gap: "15px", marginBottom: "15px" }}>
          
          <div className="filter-select-group">
            <Building size={16} className="filter-icon" />
            <select
              value={selectedBranch}
              onChange={(e) => handleBranchChange(e.target.value)}
              className="attendance-batch-select"
            >
              <option value="">-- Select Branch --</option>
              {branches.map(b => (
                <option key={b.BranchId || b.id} value={b.BranchId || b.id}>
                  {b.BranchName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-select-group">
            <BookOpen size={16} className="filter-icon" />
            <select
              value={selectedCourse}
              onChange={(e) => handleCourseChange(e.target.value)}
              className="attendance-batch-select"
              disabled={!selectedBranch}
            >
              <option value="">-- Select Course --</option>
              {courses.map(c => (
                <option key={c.BranchCourseId} value={c.BranchCourseId}>
                  {c.CourseName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-select-group">
            <Clock size={16} className="filter-icon" />
            <select
              value={selectedBatch}
              onChange={(e) => handleBatchChange(e.target.value)}
              className="attendance-batch-select"
              disabled={!selectedCourse}
            >
              <option value="">-- Select Batch --</option>
              {batches.map(b => (
                <option key={b.BatchId} value={b.BatchId}>
                  {b.BatchName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-select-group">
            <Users size={16} className="filter-icon" />
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="attendance-batch-select"
              disabled={!selectedBatch}
            >
              <option value="">-- All Students --</option>
              {batchStudents.map(s => {
                const cid = s.CourseDetailsId || s.courseDetailsId;
                return (
                  <option key={cid} value={cid}>
                    {s.StudentName || s.Name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="roster-filter-actions" style={{ flexWrap: "wrap", gap: "15px" }}>
          <div className="date-picker-group">
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>From:</span>
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="attendance-date-picker"
            />
          </div>
          <div className="date-picker-group">
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>To:</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="attendance-date-picker"
            />
          </div>

          <button 
            className="submit-attendance-btn" 
            onClick={handleGenerateReport} 
            disabled={isLoading}
            style={{ padding: "8px 20px", marginLeft: "auto" }}
          >
            {isLoading ? "Loading..." : "Generate Report"}
          </button>
        </div>
      </div>

      {/* Results Table */}
      <div className="roster-main full-width" style={{ padding: "0 20px 20px" }}>
        <div className="roster-table-card">
          <div className="table-header-toolbar">
            <h3>Report Results {reportData.length > 0 && `(${reportData.length} records found)`}</h3>
            <div className="toolbar-right-btns">
              {reportData.length > 0 && (
                <button className="export-excel-btn" onClick={handleExportExcel}>
                  <FileSpreadsheet size={16} />
                  <span>Export Report</span>
                </button>
              )}
            </div>
          </div>

          <div className="table-scroll-area">
            <table className="roster-table">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: "80px" }}>S.No</th>
                  <th>Date</th>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, idx) => {
                  const isPresent = row.isPresent !== undefined ? row.isPresent : row.IsPresent;
                  const dateStr = new Date(row.attendanceDate || row.AttendanceDate).toLocaleDateString();
                  
                  return (
                    <tr key={idx}>
                      <td className="text-center">
                        <span className="roll-number">{idx + 1}</span>
                      </td>
                      <td>
                        <strong>{dateStr}</strong>
                      </td>
                      <td>
                        <span className="student-id-txt" style={{ fontSize: "14px" }}>
                          {row.studentId || row.StudentId}
                        </span>
                      </td>
                      <td>
                        <span className="student-name">{row.studentName || row.StudentName}</span>
                      </td>
                      <td className="text-center">
                        <span className={`sleek-status-btn ${isPresent ? "present active" : "absent active"}`} style={{ padding: "4px 12px", width: "auto", borderRadius: "12px", display: "inline-flex", gap: "6px" }}>
                          {isPresent ? <><Check size={14} /> Present</> : <><X size={14} /> Absent</>}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {reportData.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan="5" className="empty-table-cell">
                      <div className="table-empty-wrap">
                        <Calendar size={32} />
                        <p>No attendance records found for the selected criteria.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
